import * as flatbuffers from 'flatbuffers';

// Must be kept in sync with `packed_slices.go` and `packed_slices.rs`!

// Encoding format:
//
//  - 1-byte encoding constant
//  - uint32 number of slices
//  - N * uint32: one "size" entry per slice, in order
//  - N * []byte; one "data" entry per slice, in order
//
// This format (in theory) allows us to fetch just the "header and index" section from GCS using a range read, then
// fetching precisely the bytes we need for an individual slice using another range read.

enum encoding {
  BIG_ENDIAN = 1, // original format; but then we realized that...
  LITTLE_ENDIAN = 2, // flatbuffers are actually defined to be little-endian, so this is the new format
}

// unpackSlices takes an encoded input buffer and returns the original set of slices it contains.
export function unpackSlices(buffer: ArrayBuffer): ArrayBuffer[] {
  const bb = new flatbuffers.ByteBuffer(new Uint8Array(buffer));
  const size = bb.capacity();
  if (size < 1) {
    throw new Error('Empty buffer');
  }

  // Check that the first byte matches the required encoding constant.
  switch (bb.readUint8(0)) {
    case encoding.BIG_ENDIAN:
      throw new Error('Big-endian no longer supported');
    case encoding.LITTLE_ENDIAN:
      // ok
      break;
    default:
      throw new Error('Invalid encoding constant');
  }

  if (size < 5) {
    throw new Error('Unexpected end of buffer');
  }

  // Read the number of slices.
  const numSlices = bb.readUint32(1);
  const slices: ArrayBuffer[] = [];

  // Start reading slice sizes at byte 5 (after the encoding constant and numSlices)
  let tableOffset = 5;
  let bufferOffset = tableOffset + 4 * numSlices;

  if (bufferOffset > size) {
    throw new Error('Unexpected end of buffer');
  }

  // Iterate over each slice
  for (let i = 0; i < numSlices; i++) {
    // Read the slice size as a uint32 from the table.
    const sliceSize = bb.readUint32(tableOffset);
    if (bufferOffset + sliceSize > size) {
      throw new Error('Unexpected end of buffer');
    }
    slices.push(buffer.slice(bufferOffset, bufferOffset + sliceSize));

    // Increment the offsets to the beginning of the next slice size and data
    tableOffset += 4;
    bufferOffset += sliceSize;
  }

  // Return the array of slices.
  return slices;
}
